import React, { useState, useEffect, useRef, useCallback } from "react";
import { Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  FiSearch, FiDollarSign, FiFileText, FiPhone,
  FiCheckCircle, FiXCircle, FiClock, FiAlertTriangle,
  FiRotateCcw, FiArrowRight,
} from "react-icons/fi";
import { apiInitiateRefund, apiGetOrderRefundStatus, apiEntitySearch } from "../../api/index.js";
import { getDeliveryLabel } from "../../utilities/paymentStatus";
import AppSelect from "../../components/ui/AppSelect";

const REFUND_REASONS = [
  "Order was cancelled by customer",
  "Order was cancelled by vendor",
  "Item(s) out of stock — could not fulfil",
  "Rider could not locate customer",
  "Duplicate / accidental order",
  "Wrong items delivered",
  "Customer dissatisfied — goodwill refund",
  "Payment collected but order never placed",
  "Other (specify below)",
];

// ── Design tokens (mirrors WalletDashboard) ──────────────────────────────────
const C = {
  bg:         "#f4f6fb",
  panel:      "#ffffff",
  header:     "#1a2b4a",
  blue:       "#2c7be5",
  blueSoft:   "#eaf2ff",
  teal:       "#00b69b",
  tealSoft:   "#e6faf7",
  orange:     "#f6961e",
  orangeSoft: "#fff4e6",
  red:        "#e63757",
  redSoft:    "#fff0f3",
  purple:     "#7048e8",
  purpleSoft: "#f1edff",
  text:       "#1a2b4a",
  textMid:    "#4a5568",
  textLight:  "#8898aa",
  border:     "#e2e8f0",
  inputBg:    "#f8faff",
  shadow:     "0 2px 12px rgba(26,43,74,0.08)",
  radius:     "10px",
  radiusSm:   "6px",
};

const REFUND_STATUS_META = {
  NONE:          { color: C.textLight,  bg: "#f0f2f5",      label: "No refund",    icon: null },
  PENDING:       { color: C.orange,     bg: C.orangeSoft,   label: "Pending",      icon: <FiClock size={12} /> },
  REFUNDED:      { color: C.teal,       bg: C.tealSoft,     label: "Refunded",     icon: <FiCheckCircle size={12} /> },
  REFUND_FAILED: { color: C.red,        bg: C.redSoft,      label: "Failed",       icon: <FiXCircle size={12} /> },
};

const fmtUGX = (n) => `UGX ${Number(n || 0).toLocaleString()}`;
const fmtDate = (d) => d ? new Date(d).toLocaleString("en-UG", { dateStyle: "medium", timeStyle: "short" }) : "—";

// ── Uganda carrier detection (mirrors Lusuku_Wallet_Service/utilities/phoneValidation.js) ──
const MTN_PREFIXES    = ["76", "77", "78", "31", "39"];
const AIRTEL_PREFIXES = ["70", "74", "75", "20"];
function detectUgandaCarrier(phone) {
  if (!phone) return null;
  const cleaned = phone.replace(/[\s\-\+\(\)]/g, "");
  let msisdn = cleaned;
  if (/^07\d{8}$/.test(cleaned))  msisdn = "256" + cleaned.substring(1);
  else if (/^7\d{8}$/.test(cleaned)) msisdn = "256" + cleaned;
  else if (!/^256\d{9}$/.test(cleaned)) return null; // too short / still typing
  const prefix = msisdn.substring(3, 5);
  if (MTN_PREFIXES.includes(prefix))    return "MTN";
  if (AIRTEL_PREFIXES.includes(prefix)) return "AIRTEL";
  return "UNKNOWN";
}

// ── Sub-components ────────────────────────────────────────────────────────────

function StepLabel({ number, label, hint }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
      <div style={{
        width: 24, height: 24, borderRadius: "50%",
        background: C.blue, color: "#fff",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 12, fontWeight: 700, flexShrink: 0,
      }}>
        {number}
      </div>
      <div>
        <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{label}</span>
        {hint && (
          <span style={{ fontSize: 11, color: C.textLight, marginLeft: 8 }}>{hint}</span>
        )}
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const meta = REFUND_STATUS_META[status] || REFUND_STATUS_META.NONE;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: "3px 10px", borderRadius: 20,
      fontSize: 11, fontWeight: 700, letterSpacing: "0.04em",
      background: meta.bg, color: meta.color,
    }}>
      {meta.icon}
      {meta.label}
    </span>
  );
}

function OrderPreviewPanel({ preview }) {
  const meta   = REFUND_STATUS_META[preview.refundStatus] || REFUND_STATUS_META.NONE;
  const isLive = preview.refundStatus === "PENDING";
  const isDone = preview.refundStatus === "REFUNDED";
  const isFail = preview.refundStatus === "REFUND_FAILED";

  return (
    <div style={{
      background: C.blueSoft,
      border: `1px solid #c8ddf8`,
      borderRadius: C.radius,
      padding: "16px 20px",
      marginBottom: 24,
    }}>
      {/* Header row */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: C.text }}>
          {preview.customerName || "—"}
        </span>
        <StatusPill status={preview.refundStatus} />
      </div>

      {/* Detail grid */}
      <div style={{
        display: "grid", gridTemplateColumns: "repeat(3, 1fr)",
        gap: "8px 16px", fontSize: 12,
      }}>
        <div>
          <div style={{ color: C.textLight, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 2, fontSize: 10 }}>Vendor</div>
          <div style={{ color: C.textMid, fontWeight: 500 }}>{preview.vendorName || "—"}</div>
        </div>
        {preview.partnerName && (
          <div>
            <div style={{ color: C.textLight, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 2, fontSize: 10 }}>3PL Partner</div>
            <div style={{ color: C.textMid, fontWeight: 500 }}>{preview.partnerName}</div>
          </div>
        )}
        <div>
          <div style={{ color: C.textLight, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 2, fontSize: 10 }}>Order Total</div>
          <div style={{ color: C.text, fontWeight: 700 }}>{fmtUGX(preview.totalPrice)}</div>
        </div>
        <div>
          <div style={{ color: C.textLight, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 2, fontSize: 10 }}>Payment</div>
          <div style={{ color: C.textMid, fontWeight: 500 }}>{preview.paymentMethod || "—"}</div>
        </div>
        <div>
          <div style={{ color: C.textLight, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 2, fontSize: 10 }}>Delivery Status</div>
          <div style={{ color: C.textMid, fontWeight: 500 }}>{preview.deliveryStatus ? getDeliveryLabel(preview.deliveryStatus) : "—"}</div>
        </div>
      </div>

      {/* Refund status note */}
      {isDone && (
        <div style={{
          marginTop: 12, padding: "8px 12px",
          background: C.tealSoft, borderRadius: C.radiusSm,
          fontSize: 12, color: C.teal, display: "flex", gap: 6, alignItems: "flex-start",
        }}>
          <FiCheckCircle size={13} style={{ marginTop: 1, flexShrink: 0 }} />
          <span>
            Refunded {fmtUGX(preview.refundAmount)} on {fmtDate(preview.refundedAt)}
            {preview.refundNote ? ` — "${preview.refundNote}"` : ""}
          </span>
        </div>
      )}
      {isLive && (
        <div style={{
          marginTop: 12, padding: "8px 12px",
          background: C.orangeSoft, borderRadius: C.radiusSm,
          fontSize: 12, color: C.orange, display: "flex", gap: 6, alignItems: "center",
        }}>
          <FiClock size={13} style={{ flexShrink: 0 }} />
          A refund disbursement is currently in progress for this order.
        </div>
      )}
      {isFail && (
        <div style={{
          marginTop: 12, padding: "8px 12px",
          background: C.redSoft, borderRadius: C.radiusSm,
          fontSize: 12, color: C.red, display: "flex", gap: 6, alignItems: "flex-start",
        }}>
          <FiAlertTriangle size={13} style={{ marginTop: 1, flexShrink: 0 }} />
          <span>Previous refund failed: {preview.refundNote || "MTN MoMo transfer error"}. You may retry below.</span>
        </div>
      )}
    </div>
  );
}

function ResultBanner({ result }) {
  if (!result) return null;
  const { success, data, message } = result;
  return (
    <div style={{
      background: success ? C.tealSoft : C.redSoft,
      border: `1px solid ${success ? "#a3e6d8" : "#f5c0cc"}`,
      borderRadius: C.radius,
      padding: "16px 20px",
      marginBottom: 20,
    }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 8,
        fontSize: 13, fontWeight: 700,
        color: success ? C.teal : C.red, marginBottom: success ? 10 : 0,
      }}>
        {success
          ? <FiCheckCircle size={15} />
          : <FiXCircle size={15} />}
        {success ? "Refund queued successfully" : "Refund initiation failed"}
      </div>
      {success && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 24px", fontSize: 12, color: C.textMid }}>
          <div>
            <span style={{ color: C.textLight, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", fontSize: 10 }}>Disbursement ID</span>
            <div style={{ fontFamily: "monospace", color: C.text, fontWeight: 600, marginTop: 2 }}>{data.disbursementId}</div>
          </div>
          <div>
            <span style={{ color: C.textLight, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", fontSize: 10 }}>Amount</span>
            <div style={{ color: C.text, fontWeight: 700, marginTop: 2 }}>{fmtUGX(data.amount)}</div>
          </div>
          <div style={{ gridColumn: "1 / -1", marginTop: 6, color: C.textLight, fontSize: 11 }}>
            The MTN MoMo transfer will complete within minutes. The customer, vendor, and 3PL partner have been notified.
          </div>
        </div>
      )}
      {!success && (
        <div style={{ fontSize: 12, color: C.textMid, marginTop: 4 }}>{message}</div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
const ProcessRefundForm = () => {
  const [orderQuery, setOrderQuery]         = useState("");
  const [orderResults, setOrderResults]     = useState([]);
  const [searchLoading, setSearchLoading]   = useState(false);
  const [selectedOrder, setSelectedOrder]   = useState(null);
  const [orderPreview, setOrderPreview]     = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const searchDebounceRef                   = useRef(null);

  const [amount, setAmount]               = useState("");
  const [reason, setReason]               = useState("");
  const [customReason, setCustomReason]   = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [isLoading, setIsLoading]         = useState(false);
  const [result, setResult]               = useState(null);
  const [confirmPending, setConfirmPending] = useState(false);

  const detectedCarrier = detectUgandaCarrier(customerPhone);
  const isMtn           = detectedCarrier === "MTN";
  const phoneComplete   = customerPhone.replace(/[\s\-\+\(\)]/g, "").length >= 10;

  // ── Debounced order search ─────────────────────────────────────────────────
  useEffect(() => {
    if (orderQuery.length < 3) { setOrderResults([]); return; }
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const res = await apiEntitySearch("ORDER", orderQuery, 8);
        setOrderResults(res.data?.results || []);
      } catch {
        setOrderResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 400);
    return () => clearTimeout(searchDebounceRef.current);
  }, [orderQuery]);

  // ── Select order ───────────────────────────────────────────────────────────
  const handleSelectOrder = useCallback(async (order) => {
    setSelectedOrder(order);
    setOrderResults([]);
    setOrderQuery(`${order.label} — ${fmtUGX(order.amount)}`);
    setAmount(String(order.amount || ""));
    setCustomerPhone(order.customerPhone || "");
    setResult(null);
    setPreviewLoading(true);
    try {
      const res = await apiGetOrderRefundStatus(order.id);
      setOrderPreview(res.data);
      // For MoMo payments, default the refund to the number that actually paid;
      // otherwise fall back to the order's customer contact number.
      if (res.data.isMomoPayment && res.data.momoPayerPhone) {
        setCustomerPhone(res.data.momoPayerPhone);
      } else if (res.data.customerPhone) {
        setCustomerPhone(res.data.customerPhone);
      }
    } catch {
      setOrderPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  // ── Reset ──────────────────────────────────────────────────────────────────
  const handleReset = () => {
    setOrderQuery(""); setSelectedOrder(null); setOrderPreview(null);
    setAmount(""); setReason(""); setCustomReason("");
    setCustomerPhone(""); setResult(null); setOrderResults([]);
  };

  // ── Submit — opens inline confirm panel ──────────────────────────────────
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectedOrder) { toast.error("Select an order first."); return; }
    const numAmount = Number(amount);
    if (!Number.isFinite(numAmount) || numAmount < 10000) { toast.error("Minimum refund amount is UGX 10,000."); return; }
    if (!reason) { toast.error("Select a refund reason."); return; }
    if (!customerPhone || customerPhone.trim().length < 9) { toast.error("Enter a valid MTN MoMo phone number."); return; }
    if (!isMtn) { toast.error("Only MTN Uganda numbers are supported. Update the phone number to proceed."); return; }
    setConfirmPending(true);
  };

  // ── Confirmed — execute refund ─────────────────────────────────────────────
  const handleConfirmedRefund = async () => {
    setConfirmPending(false);
    const numAmount = Number(amount);
    const fullReason = reason === "Other (specify below)" ? (customReason.trim() || "Other") : reason;
    setIsLoading(true);
    setResult(null);
    try {
      const res = await apiInitiateRefund({
        orderId:       selectedOrder.id,
        amount:        numAmount,
        reason:        fullReason,
        customerPhone: customerPhone.trim(),
        customerName:  orderPreview?.customerName || "",
      });
      setResult({ success: true, data: res.data });
      toast.success(`Refund initiated — ${res.data.disbursementId}`);
      try {
        const updated = await apiGetOrderRefundStatus(selectedOrder.id);
        setOrderPreview(updated.data);
      } catch {}
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "Failed to initiate refund.";
      setResult({ success: false, message: msg });
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const canRefund  = orderPreview?.canRefund !== false && !isLoading;
  const fieldStyle = (disabled) => ({
    background: disabled ? "#f0f2f5" : C.inputBg,
    border: `1px solid ${C.border}`,
    borderRadius: C.radiusSm,
    padding: "9px 12px",
    fontSize: 13,
    color: disabled ? C.textLight : C.text,
    width: "100%",
    outline: "none",
    cursor: disabled ? "not-allowed" : "text",
    transition: "border-color 0.15s",
  });
  const labelStyle = { fontSize: 11, fontWeight: 700, color: C.textLight, textTransform: "uppercase", letterSpacing: "0.07em", display: "block", marginBottom: 6 };
  const hintStyle  = { fontSize: 11, color: C.textLight, marginTop: 5, display: "block" };
  const errorStyle = { fontSize: 11, color: C.red, marginTop: 5, display: "block" };

  return (
    <div style={{ background: C.bg, minHeight: "100vh", padding: "28px 16px 48px" }}>
      <div style={{ maxWidth: 720, margin: "0 auto" }}>

        {/* ── Page header ── */}
        <div style={{
          background: `linear-gradient(135deg, ${C.header} 0%, #243b6e 100%)`,
          borderRadius: C.radius, padding: "24px 28px", marginBottom: 24,
          boxShadow: "0 4px 24px rgba(26,43,74,0.18)",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#fff", letterSpacing: "-0.3px", marginBottom: 3 }}>
              Process Customer Refund
            </div>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.65)" }}>
              Issue an MTN MoMo disbursement refund for a cancelled or disputed order
            </div>
          </div>
          {selectedOrder && (
            <button
              type="button"
              onClick={handleReset}
              style={{
                background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)",
                color: "#fff", borderRadius: C.radiusSm, padding: "7px 14px",
                fontSize: 12, fontWeight: 600, cursor: "pointer",
                display: "flex", alignItems: "center", gap: 6,
              }}
            >
              <FiRotateCcw size={13} /> Reset
            </button>
          )}
        </div>

        {/* ── Form panel ── */}
        <div style={{
          background: C.panel, borderRadius: C.radius,
          boxShadow: C.shadow, border: `1px solid ${C.border}`,
          overflow: "visible",
        }}>

          {/* Result banner at top */}
          {result && (
            <div style={{ padding: "20px 28px 0" }}>
              <ResultBanner result={result} />
            </div>
          )}

          <div style={{ padding: "24px 28px", display: "flex", flexDirection: "column", gap: 24 }}>

            {/* ── Step 1: Order Search ── */}
            <div>
              <StepLabel number={1} label="Search Order" hint="by Order ID, customer name, or phone" />
              <div style={{ position: "relative" }}>
                <div style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", color: C.textLight, pointerEvents: "none", zIndex: 1 }}>
                  <FiSearch size={14} />
                </div>
                <input
                  type="text"
                  placeholder="Type order ID, customer name or phone..."
                  value={orderQuery}
                  onChange={(e) => {
                    setOrderQuery(e.target.value);
                    if (selectedOrder) { setSelectedOrder(null); setOrderPreview(null); }
                  }}
                  autoComplete="off"
                  style={{ ...fieldStyle(false), paddingLeft: 34 }}
                />
                {searchLoading && (
                  <div style={{ position: "absolute", right: 11, top: "50%", transform: "translateY(-50%)", zIndex: 1 }}>
                    <Spinner animation="border" size="sm" style={{ color: C.blue, width: 14, height: 14 }} />
                  </div>
                )}

              {/* Results dropdown */}
              {orderResults.length > 0 && (
                <div style={{
                  position: "absolute", left: 0, right: 0, zIndex: 400,
                  background: C.panel, border: `1px solid ${C.border}`,
                  borderTop: "none", borderRadius: `0 0 ${C.radiusSm} ${C.radiusSm}`,
                  boxShadow: "0 8px 24px rgba(26,43,74,0.12)",
                  maxHeight: 260, overflowY: "auto",
                }}>
                  {orderResults.map((o, idx) => (
                    <div
                      key={o.id}
                      style={{
                        padding: "10px 14px", cursor: "pointer",
                        borderBottom: idx < orderResults.length - 1 ? `1px solid ${C.border}` : "none",
                        transition: "background 0.1s",
                        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = C.blueSoft)}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                    >
                      <div style={{ flex: 1, minWidth: 0 }} onClick={() => handleSelectOrder(o)}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{o.label}</span>
                          <FiArrowRight size={12} style={{ color: C.textLight, flexShrink: 0 }} />
                        </div>
                        <div style={{ fontSize: 11, color: C.textLight, marginTop: 2 }}>{o.subLabel}</div>
                      </div>
                      <button
                        type="button"
                        title="Copy Order ID"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigator.clipboard.writeText(o.id.toString()).then(() => {
                            toast.success(`Order ID copied`);
                          });
                        }}
                        style={{
                          flexShrink: 0,
                          background: "transparent", border: `1px solid ${C.border}`,
                          borderRadius: 4, padding: "3px 7px",
                          fontSize: 10, color: C.textMid, cursor: "pointer",
                          fontFamily: "monospace", letterSpacing: 0,
                          whiteSpace: "nowrap",
                        }}
                      >
                        copy id
                      </button>
                    </div>
                  ))}
                </div>
              )}
              </div> {/* end position:relative wrapper */}
            </div>

            {/* ── Order preview panel ── */}
            {previewLoading && (
              <div style={{ display: "flex", alignItems: "center", gap: 10, color: C.textLight, fontSize: 13, padding: "4px 0" }}>
                <Spinner animation="border" size="sm" style={{ color: C.blue, width: 14, height: 14 }} />
                Loading order details...
              </div>
            )}
            {orderPreview && !previewLoading && (
              <OrderPreviewPanel preview={orderPreview} />
            )}

            {/* ── Divider ── */}
            {selectedOrder && (
              <div style={{ borderTop: `1px solid ${C.border}`, margin: "0 -28px" }} />
            )}

            {/* ── Step 2: Amount ── */}
            <div>
              <StepLabel number={2} label="Refund Amount" />
              <label style={labelStyle}>Amount (UGX)</label>
              <div style={{ display: "flex", alignItems: "center", gap: 0, position: "relative" }}>
                <div style={{
                  position: "absolute", left: 0, top: 0, bottom: 0,
                  width: 38, display: "flex", alignItems: "center", justifyContent: "center",
                  background: "#edf2f7", borderRadius: `${C.radiusSm} 0 0 ${C.radiusSm}`,
                  border: `1px solid ${C.border}`, borderRight: "none",
                  color: C.textMid,
                }}>
                  <FiDollarSign size={13} />
                </div>
                <input
                  type="number"
                  min={10000}
                  step={1}
                  placeholder="e.g. 15000"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  disabled={!selectedOrder || !canRefund}
                  style={{ ...fieldStyle(!selectedOrder || !canRefund), paddingLeft: 46, borderRadius: `0 ${C.radiusSm} ${C.radiusSm} 0` }}
                />
              </div>
              {orderPreview && amount && Number(amount) > orderPreview.totalPrice && (
                <span style={errorStyle}>
                  Amount exceeds order total ({fmtUGX(orderPreview.totalPrice)})
                </span>
              )}
              {amount && Number(amount) < 10000 && (
                <span style={errorStyle}>Minimum refund is UGX 10,000</span>
              )}
              {orderPreview && amount && Number(amount) > 0 && Number(amount) <= orderPreview.totalPrice && (
                <span style={hintStyle}>
                  Partial refund: {fmtUGX(amount)} of {fmtUGX(orderPreview.totalPrice)} order total
                </span>
              )}
            </div>

            {/* ── Step 3: Reason ── */}
            <div>
              <StepLabel number={3} label="Reason for Refund" />
              <label style={labelStyle}>Select reason</label>
              <AppSelect
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                disabled={!selectedOrder || !canRefund}
                style={{ ...fieldStyle(!selectedOrder || !canRefund), appearance: "none" }}
              >
                <option value="">— Select a reason —</option>
                {REFUND_REASONS.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </AppSelect>

              {reason === "Other (specify below)" && (
                <div style={{ marginTop: 12 }}>
                  <label style={labelStyle}>
                    <FiFileText size={11} style={{ marginRight: 4 }} />
                    Specify reason
                  </label>
                  <textarea
                    rows={2}
                    value={customReason}
                    onChange={(e) => setCustomReason(e.target.value)}
                    placeholder="Describe the reason for the refund..."
                    style={{ ...fieldStyle(false), resize: "vertical", minHeight: 64 }}
                  />
                </div>
              )}
            </div>

            {/* ── Step 4: MTN Phone ── */}
            <div>
              <StepLabel
                number={4}
                label="Customer MTN MoMo Number"
                hint={detectedCarrier ? (
                  isMtn ? "MTN Uganda ✔" : detectedCarrier === "AIRTEL" ? "⚠️ Airtel detected" : "⚠️ Unknown carrier"
                ) : "must be an active MTN Uganda line"}
              />
              <label style={labelStyle}>Phone number</label>
              <div style={{ position: "relative" }}>
                <div style={{
                  position: "absolute", left: 0, top: 0, bottom: 0,
                  width: 38, display: "flex", alignItems: "center", justifyContent: "center",
                  background: phoneComplete && !isMtn ? "#fef3f3" : "#edf2f7",
                  borderRadius: `${C.radiusSm} 0 0 ${C.radiusSm}`,
                  border: `1px solid ${phoneComplete && !isMtn ? C.red : C.border}`, borderRight: "none",
                  color: phoneComplete && !isMtn ? C.red : C.textMid,
                }}>
                  <FiPhone size={13} />
                </div>
                <input
                  type="tel"
                  placeholder="0771234567 or 256771234567"
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(e.target.value)}
                  disabled={!selectedOrder || !canRefund}
                  style={{
                    ...fieldStyle(!selectedOrder || !canRefund),
                    paddingLeft: 46,
                    borderRadius: `0 ${C.radiusSm} ${C.radiusSm} 0`,
                    ...(phoneComplete && !isMtn && { borderColor: C.red, background: "#fff8f8" }),
                  }}
                />
              </div>

              {/* MoMo payer match feedback */}
              {orderPreview?.isMomoPayment && orderPreview?.momoPayerPhone && (
                <div style={{ marginTop: 6, fontSize: 11, color: C.textMid, display: "flex", alignItems: "center", gap: 6 }}>
                  {customerPhone.replace(/\D/g, "").endsWith(orderPreview.momoPayerPhone.replace(/\D/g, "").slice(-9))
                    ? <><FiCheckCircle size={12} style={{ color: C.teal }} /> Matches the MoMo number that paid for this order.</>
                    : <><FiAlertTriangle size={12} style={{ color: C.orange }} /> Differs from the paying MoMo number ({orderPreview.momoPayerPhone}). Confirm before refunding.</>}
                </div>
              )}

              {/* Carrier detection feedback */}
              {phoneComplete && isMtn && (
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6, fontSize: 12, color: C.teal, fontWeight: 600 }}>
                  <FiCheckCircle size={12} /> MTN Uganda — eligible for MoMo disbursement
                </div>
              )}
              {phoneComplete && detectedCarrier === "AIRTEL" && (
                <div style={{
                  marginTop: 8, padding: "10px 14px",
                  background: "#fff8f0", border: `1px solid #f9d8a0`,
                  borderRadius: C.radiusSm, fontSize: 12,
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 700, color: C.orange, marginBottom: 4 }}>
                    <FiAlertTriangle size={13} /> Airtel number detected — cannot disburse
                  </div>
                  <div style={{ color: C.textMid, lineHeight: 1.5 }}>
                    The MTN MoMo Disbursements API only supports MTN Uganda numbers.
                    Please enter an alternative <strong>MTN number</strong> for this customer
                    (e.g. ask them via support or check the order notes).
                    If no MTN number is available, a cash or manual arrangement is required.
                  </div>
                </div>
              )}
              {phoneComplete && detectedCarrier === "UNKNOWN" && (
                <div style={{
                  marginTop: 8, padding: "10px 14px",
                  background: C.redSoft, border: `1px solid #f9c0cc`,
                  borderRadius: C.radiusSm, fontSize: 12,
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 700, color: C.red, marginBottom: 4 }}>
                    <FiAlertTriangle size={13} /> Unrecognised Uganda number
                  </div>
                  <div style={{ color: C.textMid }}>
                    This number does not match any known Uganda network prefix. Verify the number and try again.
                  </div>
                </div>
              )}
              {!phoneComplete && (
                <span style={hintStyle}>
                  Pre-filled from the order's customer record. Override if sending to a different number.
                </span>
              )}
            </div>

            {/* ── Inline confirm panel ── */}
            {confirmPending && (
              <div style={{
                background: "#1a2b4a", borderRadius: C.radius,
                padding: "18px 20px", display: "flex", flexDirection: "column", gap: 14,
              }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#fff", display: "flex", alignItems: "center", gap: 8 }}>
                  <FiAlertTriangle size={15} style={{ color: "#f6961e", flexShrink: 0 }} />
                  Confirm refund — please review before proceeding
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {[
                    ["Customer",   orderPreview?.customerName || "—"],
                    ["Phone",      customerPhone],
                    ["Amount",     fmtUGX(Number(amount))],
                    ["Reason",     reason === "Other (specify below)" ? (customReason || "Other") : reason],
                    ["Order",      orderPreview?.orderRef || selectedOrder?.label || "—"],
                    ["Order date", orderPreview?.createdAt ? fmtDate(orderPreview.createdAt) : "—"],
                  ].map(([k, v]) => (
                    <div key={k} style={{ display: "flex", gap: 10, fontSize: 12 }}>
                      <span style={{ color: "rgba(255,255,255,0.5)", minWidth: 72, flexShrink: 0 }}>{k}</span>
                      <span style={{ color: "#fff", fontWeight: 600, wordBreak: "break-all" }}>{v}</span>
                    </div>
                  ))}
                </div>
                <div style={{ display: "flex", gap: 10 }}>
                  <button
                    type="button"
                    onClick={handleConfirmedRefund}
                    style={{
                      background: C.teal, color: "#fff", border: "none",
                      borderRadius: C.radiusSm, padding: "9px 22px",
                      fontSize: 13, fontWeight: 700, cursor: "pointer",
                      display: "inline-flex", alignItems: "center", gap: 7,
                    }}
                  >
                    <FiCheckCircle size={13} /> Yes, issue refund
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmPending(false)}
                    style={{
                      background: "rgba(255,255,255,0.1)", color: "#fff",
                      border: "1px solid rgba(255,255,255,0.2)",
                      borderRadius: C.radiusSm, padding: "9px 18px",
                      fontSize: 13, fontWeight: 600, cursor: "pointer",
                      display: "inline-flex", alignItems: "center", gap: 7,
                    }}
                  >
                    <FiXCircle size={13} /> Cancel
                  </button>
                </div>
              </div>
            )}

            {/* ── Submit area ── */}
            <div style={{ display: "flex", alignItems: "center", gap: 12, paddingTop: 4 }}>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!selectedOrder || !canRefund || isLoading || (phoneComplete && !isMtn)}
                style={{
                  background: (!selectedOrder || !canRefund || isLoading || (phoneComplete && !isMtn)) ? "#c8d6ea" : C.blue,
                  color: "#fff", border: "none",
                  borderRadius: C.radiusSm, padding: "10px 24px",
                  fontSize: 13, fontWeight: 700, cursor: (!selectedOrder || !canRefund || isLoading || (phoneComplete && !isMtn)) ? "not-allowed" : "pointer",
                  display: "inline-flex", alignItems: "center", gap: 8,
                  transition: "background 0.15s", minWidth: 160,
                }}
              >
                {isLoading ? (
                  <>
                    <Spinner animation="border" size="sm" style={{ width: 13, height: 13 }} />
                    Processing...
                  </>
                ) : (
                  <>
                    <FiCheckCircle size={14} />
                    Issue Refund
                  </>
                )}
              </button>

              {selectedOrder && !isLoading && (
                <button
                  type="button"
                  onClick={handleReset}
                  style={{
                    background: "transparent", color: C.textMid,
                    border: `1px solid ${C.border}`, borderRadius: C.radiusSm,
                    padding: "10px 18px", fontSize: 13, fontWeight: 600,
                    cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6,
                  }}
                >
                  <FiRotateCcw size={13} /> Reset
                </button>
              )}
            </div>

            {/* Blocked-state notices */}
            {!canRefund && orderPreview?.refundStatus === "REFUNDED" && (
              <div style={{
                background: C.tealSoft, border: `1px solid #a3e6d8`,
                borderRadius: C.radiusSm, padding: "10px 14px",
                fontSize: 12, color: C.teal, display: "flex", alignItems: "center", gap: 8,
              }}>
                <FiCheckCircle size={13} />
                This order has already been fully refunded.
              </div>
            )}
            {!canRefund && orderPreview?.refundStatus === "PENDING" && (
              <div style={{
                background: C.orangeSoft, border: `1px solid #f9d8a0`,
                borderRadius: C.radiusSm, padding: "10px 14px",
                fontSize: 12, color: C.orange, display: "flex", alignItems: "center", gap: 8,
              }}>
                <FiClock size={13} />
                A refund disbursement is already in progress for this order.
              </div>
            )}

          </div>

          {/* ── Footer ── */}
          <div style={{
            borderTop: `1px solid ${C.border}`,
            padding: "12px 28px",
            fontSize: 11, color: C.textLight,
            display: "flex", alignItems: "center", gap: 6,
          }}>
            <FiAlertTriangle size={11} />
            Refunds are disbursed via MTN MoMo. All parties are notified in real-time. Lusuku absorbs the transfer fee.
          </div>
        </div>

      </div>
    </div>
  );
};

export default ProcessRefundForm;
